import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { ConfirmDialogResponse, MarknadsprislistaTyp } from "~app/common/models/constants";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "~app/common/validation/validationContext";
import { ValidationMessageContainerComponent } from "../../../../common/validation/validation-message-container/validation-message-container.component";
import { ValidationRuleDirective } from "../../../../common/validation/validation-rule.directive";
import { IHandelssortimentsgrupp, IMarknadsprislista } from "../../models/marknadsprislista-model";
import { IMarknadsomrade } from "../../models/verksamhetsomraden-models";
import { MarknadsprislistaService } from "../../services/marknadsprislista.service";
import { MarknadsprislistaValidation } from "./marknadsprislista-validation";

@Component({
    selector: "rask-marknadsprislista-dialog",
    templateUrl: "./marknadsprislista-dialog.component.html",
    styleUrls: ["./marknadsprislista-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        ValidationRuleDirective,
        CheckboxComponent,
        DatepickerComponent,
        MultiSelectorComponent,
        RadiobuttonGroupComponent,
        ValidationMessageContainerComponent,
        ButtonComponent
    ]
})
export class MarknadsprislistaDialogComponent implements OnInit, OnDestroy {
    readonly activeModal = inject(NgbActiveModal);
    anvandsFor: IRadiobuttonItem[] = [
        {
            key: MarknadsprislistaTyp.Viol2,
            value: MarknadsprislistaTyp.AllaWithKeys[MarknadsprislistaTyp.Viol2]
        },
        {
            key: MarknadsprislistaTyp.Viol3,
            value: MarknadsprislistaTyp.AllaWithKeys[MarknadsprislistaTyp.Viol3]
        },
        {
            key: MarknadsprislistaTyp.Skordarmatning,
            value: MarknadsprislistaTyp.AllaWithKeys[MarknadsprislistaTyp.Skordarmatning]
        }
    ];

    data: {
        marknadsomrade: IMarknadsomrade[];
        handelssortimentGrupper: IHandelssortimentsgrupp[];
        prislista: IMarknadsprislista;
    };
    form: UntypedFormGroup;
    handelssortimentGrupper: IHandelssortimentsgrupp[];
    marknadsomrade: IMarknadsomrade[];
    prislista: any;
    prislistaForChange: any;
    validationContext: ValidationContext;

    private readonly busyService = inject(BusyService);
    private readonly destroy$ = new Subject<void>();
    private readonly dirtyFormService = inject(DirtyFormService);

    private readonly formBinder = inject(FormBinderService);
    private readonly marknadsprislistaService = inject(MarknadsprislistaService);
    private readonly raskModalService = inject(RaskModalService);

    canSave() {
        return this.form.valid;
    }

    close(dismiss: boolean) {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (dismiss) {
            this.activeModal.close(null);
        } else {
            this.activeModal.close(this.prislista);
        }
    }

    dirtyCheckCallback(result: IDirtyFormResult) {
        if (result.isSkipSave) {
            // close and dismiss
            this.close(true);
        } else if (result.isCancel) {
            // do nothing
        } else {
            // save
            this.save();
        }
    }

    getHandelssortimentsgrupper() {
        if (this.handelssortimentGrupper != null) {
            return of(this.handelssortimentGrupper);
        }
        return null;
    }

    getMarknadsomrade() {
        if (this.marknadsomrade != null) {
            return of(this.marknadsomrade);
        }
        return null;
    }

    isLoaded = () => this.prislistaForChange != null && this.form != null && this.validationContext != null;

    isNew() {
        return this.prislista.id == null || this.prislista.id === 0;
    }

    loadPrislista() {
        // Deep copy för att inte ändra i bas objektet;
        this.prislistaForChange = JSON.parse(JSON.stringify(this.prislista));
        this.form = this.formBinder.CreateGroup(this.prislistaForChange);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit() {
        this.marknadsomrade = this.data.marknadsomrade;
        this.handelssortimentGrupper = this.data.handelssortimentGrupper;
        this.prislista = this.data.prislista;

        this.loadPrislista();

        this.validationContext = MarknadsprislistaValidation.init(this.prislistaForChange);
        this.validationContext.scope(ValidationScopes.spara, ValidationErrorLevels.error);

        this.form.valueChanges.pipe(
            takeUntil(this.destroy$)
        ).subscribe(this.onFormChanges);

        this.dirtyFormService.onDirty(this.form,
            () => this.save(),
            d => this.dirtyCheckCallback(d));
    }

    radera() {
        this.raskModalService.confirm(new ModalSettings("Bekräfta", "Ta bort marknadsprislista?")
        )
            .subscribe((res) => {
                if (res === ConfirmDialogResponse.save) {
                    const query = this.marknadsprislistaService.deleteMarknadsprislista(this.prislista.id);
                    this.busyService.addWork(query).subscribe(() => {
                        this.form.markAsPristine();
                        this.prislista = {
                            id: this.prislista.id,
                            deleted: true
                        };
                        this.tryClose();
                    });
                }
            });
    }

    save() {
        this.validationContext.scope(ValidationScopes.spara, ValidationErrorLevels.error).subscribe(scope => {
            if (this.validationContext.validate(true)) {
                this.prislista = this.prislistaForChange;

                if (this.prislista.viol3) {
                    this.prislista.vidLeveransGallande = false;
                }
                const query = this.marknadsprislistaService.saveMarknadsprislista(this.prislista);
                this.busyService.addWork(query).subscribe((res: any) => {
                    this.prislista.id = res.id;
                    this.form.markAsPristine();
                    this.tryClose();
                });
            }
        });
    }

    selectorFormatter = (v: any) => v.namn;

    selectorGroupBy = (v: any) => v.namn;

    selectorOutputFormatter = (v: any) => v ? v.namn : null;

    shouldShowVLG() {
        return this.prislistaForChange.typ === MarknadsprislistaTyp.Viol2;
    }

    tryClose(clear = false) {
        if (this.dirtyFormService.canClose()) {
            if (clear) {
                this.close(true);
            }
            this.close(false);
        }
    }

    private readonly onFormChanges = (): void => {
        this.validationContext.validate(true);
    };
}
